/**
 * Estimated drawdown on the currently open book.
 *
 * Two independent views of the same positions:
 *  - scenario: what an adverse move of N pips costs, swept into a stress curve.
 *  - stop loss: what the orders' own stops cap the loss at, and which orders
 *    have no stop and are therefore unbounded.
 */
/**
 * A "pip" is 0.0001 for most pairs and 0.01 for JPY-quoted ones. This only
 * affects how the x-axis is labelled — the money maths runs on raw price
 * movement, so a wrong guess here cannot distort the loss figures.
 */
export const pipSize = (pair) => (pair.toUpperCase().endsWith('JPY') ? 0.01 : 0.0001);
/** Loss (negative) on one position if price moves `pips` against it. */
export const positionLossAtPips = (position, pips) => {
    if (position.valueFactor === null)
        return null;
    const priceMove = pips * pipSize(position.pair);
    return -(priceMove * position.lots * position.valueFactor);
};
export const DEFAULT_SCENARIO_PIPS = [0, 50, 100, 200, 300, 500, 750, 1000];
/**
 * Sweep the adverse-move scenarios into a curve, per pair and in total.
 *
 * Every open position is treated as moving against itself simultaneously —
 * the correlated worst case. Real books rarely move fully in lockstep, so this
 * is deliberately a pessimistic bound rather than an expectation.
 */
export const buildScenarioCurve = (positions, equity, pipSteps) => pipSteps.map((pips) => {
    const point = { pips, loss: 0, lossPct: null };
    let total = 0;
    for (const position of positions) {
        const loss = positionLossAtPips(position, pips);
        if (loss === null)
            continue;
        point[position.pair] = (point[position.pair] ?? 0) + loss;
        total += loss;
    }
    point.loss = total;
    point.lossPct = equity && equity > 0 ? (total / equity) * 100 : null;
    return point;
});
/** Loss if this position's own stop were hit. Null when no stop is set. */
export const positionLossAtStop = (position) => {
    if (position.valueFactor === null)
        return null;
    if (position.stopLoss === null || position.stopLoss === 0)
        return null;
    // Buy (0) loses as price falls to the stop; sell (1) loses as it rises.
    const priceMove = position.type === 0 ? position.openPrice - position.stopLoss : position.stopLoss - position.openPrice;
    // A stop on the profitable side caps a gain, not a loss.
    if (priceMove <= 0)
        return 0;
    return -(priceMove * position.lots * position.valueFactor);
};
export const buildPairExposures = (positions, referencePips) => {
    const byPair = new Map();
    for (const position of positions) {
        let row = byPair.get(position.pair);
        if (!row) {
            row = {
                pair: position.pair,
                positions: 0,
                lots: 0,
                floatingProfit: 0,
                scenarioLoss: 0,
                stopLoss: 0,
                ordersWithoutStop: 0,
            };
            byPair.set(position.pair, row);
        }
        row.positions += 1;
        row.lots += position.lots;
        row.floatingProfit += position.floatingProfit;
        const scenario = positionLossAtPips(position, referencePips);
        row.scenarioLoss = scenario === null || row.scenarioLoss === null ? null : row.scenarioLoss + scenario;
        const stop = positionLossAtStop(position);
        if (stop === null) {
            row.ordersWithoutStop += 1;
            // One unprotected order makes the pair's floor unbounded.
            row.stopLoss = null;
        }
        else if (row.stopLoss !== null) {
            row.stopLoss += stop;
        }
    }
    return Array.from(byPair.values()).sort((a, b) => {
        const aLoss = a.scenarioLoss ?? 0;
        const bLoss = b.scenarioLoss ?? 0;
        return aLoss - bLoss;
    });
};
export const buildDrawdownSummary = (positions, equity, referencePips) => {
    const exposures = buildPairExposures(positions, referencePips);
    const unpricedPairs = Array.from(new Set(positions.filter((p) => p.valueFactor === null).map((p) => p.pair))).sort();
    let scenarioLoss = 0;
    let stopLoss = 0;
    let ordersWithoutStop = 0;
    for (const exposure of exposures) {
        if (exposure.scenarioLoss === null)
            scenarioLoss = null;
        else if (scenarioLoss !== null)
            scenarioLoss += exposure.scenarioLoss;
        if (exposure.stopLoss === null)
            stopLoss = null;
        else if (stopLoss !== null)
            stopLoss += exposure.stopLoss;
        ordersWithoutStop += exposure.ordersWithoutStop;
    }
    const pct = (value) => value !== null && equity && equity > 0 ? (value / equity) * 100 : null;
    return {
        openPositions: positions.length,
        totalLots: positions.reduce((sum, p) => sum + p.lots, 0),
        floatingProfit: positions.reduce((sum, p) => sum + p.floatingProfit, 0),
        scenarioLoss,
        scenarioLossPct: pct(scenarioLoss),
        stopLoss,
        stopLossPct: pct(stopLoss),
        ordersWithoutStop,
        unpricedPairs,
    };
};
